import type { Category, CategorizeGroup, MainCategory, SearchFilterOptions } from '../types';
import type { PrimeCategory, Subcategory } from '../types';

// Exact word matching
export const isExactWordMatch = (text: string, query: string): boolean => {
  if (!query || !query.trim()) return true;
  const cleanText = text.trim();
  const cleanQuery = query.trim();
  const regexPattern = new RegExp(`\\b${escapeRegex(cleanQuery)}\\b`, 'gi');
  return regexPattern.test(cleanText);
};

const escapeRegex = (str: string): string => {
  return str.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
};

export const containsExactWord = (text: string, query: string): boolean => {
  if (!query || !query.trim()) return true;
  const words = text.toLowerCase().split(/\s+/);
  const queryLower = query.toLowerCase().trim();
  return words.some(word => word === queryLower);
};

export const normalizeText = (text: string): string => {
  return text.toLowerCase().trim().replace(/\s+/g, ' ');
};

// ============================================================================
// Prime / Subcategory hierarchy
// ============================================================================

export const PRIME_CATEGORIES: PrimeCategory[] = [
  {
    id: 'income',
    name: 'Income',
    icon: 'M12 2v20M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6',
    color: '#10b981',
    bgClass: 'bg-emerald-500/10',
    textClass: 'text-emerald-300',
    borderClass: 'border-emerald-500/30',
  },
  {
    id: 'expenditure',
    name: 'Expenditure',
    icon: 'M3 3h18v18H3zM21 9H3M9 21V9',
    color: '#f43f5e',
    bgClass: 'bg-rose-500/10',
    textClass: 'text-rose-300',
    borderClass: 'border-rose-500/30',
  },
  {
    id: 'loan',
    name: 'Loan',
    icon: 'M19 14c1.49-1.46 3-3.21 3-5.5A5.5 5.5 0 0 0 16.5 3c-1.76 0-3.3.78-4.34 2H5.83A2 2 0 0 0 2 7.17v11.17A2 2 0 0 0 5.83 21h11.17a2 2 0 0 0 1.83-1.17L21 14zM16.5 6.5a3.5 3.5 0 0 1 3.5 3.5c0 1.5-.8 2.8-2 3.5',
    color: '#f59e0b',
    bgClass: 'bg-amber-500/10',
    textClass: 'text-amber-300',
    borderClass: 'border-amber-500/30',
  },
  {
    id: 'investment',
    name: 'Investment',
    icon: 'M3 3v18h18M18 9l-5 5-4-4-3 3',
    color: '#06b6d4',
    bgClass: 'bg-cyan-500/10',
    textClass: 'text-cyan-300',
    borderClass: 'border-cyan-500/30',
  },
  {
    id: 'others',
    name: 'Others',
    icon: 'M21 8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16Z',
    color: '#64748b',
    bgClass: 'bg-slate-500/10',
    textClass: 'text-slate-300',
    borderClass: 'border-slate-500/30',
  },
];

export const BUILTIN_SUBCATEGORIES: Record<string, { id: string; name: string }[]> = {
  income: [
    { id: 'salary', name: 'Salary' },
    { id: 'bonus', name: 'Bonus / Incentive' },
    { id: 'interest', name: 'Interest / Dividends' },
    { id: 'freelance', name: 'Freelance' },
    { id: 'rental', name: 'Rental Income' },
  ],
  expenditure: [
    { id: 'food', name: 'Food & Dining' },
    { id: 'bills', name: 'Bills & Utilities' },
    { id: 'shopping', name: 'Shopping' },
    { id: 'subscription', name: 'Subscriptions' },
    { id: 'transport', name: 'Transport' },
    { id: 'rent', name: 'Rent' },
    { id: 'medical', name: 'Medical / Health' },
    { id: 'education', name: 'Education' },
  ],
  loan: [
    { id: 'emi', name: 'EMI' },
    { id: 'credit-card', name: 'Credit Card' },
    { id: 'personal-loan', name: 'Personal Loan' },
    { id: 'home-loan', name: 'Home Loan' },
  ],
  investment: [
    { id: 'mutual-funds', name: 'Mutual Funds' },
    { id: 'stocks', name: 'Stocks / Equity' },
    { id: 'fd-rd', name: 'FD / RD' },
    { id: 'gold', name: 'Gold' },
    { id: 'crypto', name: 'Crypto' },
  ],
  others: [
    { id: 'charity', name: 'Charity / Donations' },
    { id: 'gifts', name: 'Gifts' },
    { id: 'pets', name: 'Pets' },
    { id: 'misc', name: 'Miscellaneous' },
  ],
};

export const LEGACY_TO_PRIME: Record<string, string> = {
  // Expenditure subcategories
  Food: 'expenditure', 'Food & Dining': 'expenditure',
  Bills: 'expenditure', 'Bills & Utilities': 'expenditure',
  Shopping: 'expenditure',
  Subscription: 'expenditure', Subscriptions: 'expenditure',
  Transport: 'expenditure', Rent: 'expenditure',
  Medical: 'expenditure', 'Medical / Health': 'expenditure',
  Education: 'expenditure',
  // Income subcategories
  Income: 'income', Salary: 'income', 'Bonus / Incentive': 'income', Bonus: 'income',
  'Interest / Dividends': 'income', Interest: 'income',
  Freelance: 'income', 'Rental Income': 'income', Rental: 'income',
  // Investment subcategories
  Investment: 'investment', 'Mutual Funds': 'investment',
  'Stocks / Equity': 'investment', Stocks: 'investment',
  'FD / RD': 'investment', FD: 'investment',
  Gold: 'investment', Crypto: 'investment',
  // Loan subcategories
  Loan: 'loan', EMI: 'loan',
  'Credit Card': 'loan', 'Personal Loan': 'loan', 'Home Loan': 'loan',
  // Others
  Other: 'others', Charity: 'others', 'Charity / Donations': 'others',
  Gifts: 'others', Pets: 'others', Miscellaneous: 'others', Misc: 'others',
};

export const getPrimeCategory = (id: string): PrimeCategory | undefined =>
  PRIME_CATEGORIES.find(p => p.id === id);

export const getSubcategoriesForPrime = (
  primeId: string,
  custom: string[] = [],
  customMap: Record<string, string> = {},
): Subcategory[] => {
  const builtins = (BUILTIN_SUBCATEGORIES[primeId] || []).map(s => ({
    id: s.id,
    name: s.name,
    parentId: primeId,
    isCustom: false,
  }));
  const customs = custom
    .filter(name => (customMap[name] || LEGACY_TO_PRIME[name] || '').toLowerCase() === primeId)
    .map((name) => ({
      id: `custom-${name.toLowerCase().replace(/\s+/g, '-')}`,
      name,
      parentId: primeId,
      isCustom: true,
    }));
  return [...builtins, ...customs];
};

export const findPrimeForSubcategory = (
  name: string,
  customMap: Record<string, string> = {},
): string | undefined => {
  const lower = name.toLowerCase();
  if (customMap[lower]) return customMap[lower].toLowerCase();
  if (LEGACY_TO_PRIME[name]) return LEGACY_TO_PRIME[name].toLowerCase();
  for (const [primeId, subs] of Object.entries(BUILTIN_SUBCATEGORIES)) {
    if (subs.some(s => s.name.toLowerCase() === lower || s.id === lower)) {
      return primeId;
    }
  }
  return 'others';
};

export const smartSearch = (query: string): (PrimeCategory | Subcategory)[] => {
  if (!query || !query.trim()) return [];
  const q = query.toLowerCase().trim();
  const results: (PrimeCategory | Subcategory)[] = [];

  for (const prime of PRIME_CATEGORIES) {
    if (prime.name.toLowerCase().includes(q)) {
      results.push(prime);
    }
    for (const sub of (BUILTIN_SUBCATEGORIES[prime.id] || [])) {
      if (sub.name.toLowerCase().includes(q)) {
        results.push({ ...sub, parentId: prime.id, isCustom: false });
      }
    }
  }
  return results;
};

// ============================================================================
// Legacy compat
// ============================================================================

export const buildCategoryHierarchy = (
  mainCategories: Category[],
  flatCategories: string[],
  _customMap: Record<string, string> = {},
): MainCategory[] => {
  const result: MainCategory[] = mainCategories.map(main => ({
    id: main.id,
    name: main.name,
    isMain: true as const,
    parentId: undefined,
    subcategories: [] as Category[],
    color: main.color,
  }));

  flatCategories.forEach(catName => {
    const normalizedCat = normalizeText(catName);
    const mainCategory = result.find(main =>
      normalizedCat.includes(normalizeText(main.name)) ||
      main.name.toLowerCase() === normalizedCat
    );

    if (mainCategory) {
      mainCategory.subcategories = mainCategory.subcategories || [];
      if (!mainCategory.subcategories.find(sc => normalizeText(sc.name) === normalizedCat)) {
        mainCategory.subcategories.push({
          id: catName.toLowerCase().replace(/\s+/g, '-'),
          name: catName,
          isMain: true,
          parentId: mainCategory.id,
          subcategories: [],
        });
      }
    }
  });

  return result;
};

export const filterCategories = (
  _hierarchy: MainCategory[],
  options: SearchFilterOptions,
  groups: CategorizeGroup[],
): CategorizeGroup[] => {
  let filtered = [...groups];

  if (options.primeCategoryId) {
    filtered = filtered.filter(g => g.current_prime_category === options.primeCategoryId);
  }

  if (options.query && options.query.trim()) {
    const queryStr = options.query;
    filtered = filtered.filter(g =>
      isExactWordMatch(g.merchant, queryStr) ||
      isExactWordMatch(g.current_category, queryStr)
    );
  }

  if (options.showUncategorizedOnly) {
    filtered = filtered.filter(g =>
      g.current_category === 'Other' || g.current_category === 'Others' || !g.current_category
    );
  }

  if (options.sortBy) {
    filtered.sort((a, b) => {
      let comparison = 0;
      switch (options.sortBy) {
        case 'name':
          comparison = a.merchant.localeCompare(b.merchant);
          break;
        case 'count':
          comparison = a.count - b.count;
          break;
        case 'total':
          comparison = a.total - b.total;
          break;
        case 'date':
          comparison = new Date(b.last_date).getTime() - new Date(a.last_date).getTime();
          break;
      }
      return options.sortOrder === 'desc' ? -comparison : comparison;
    });
  }

  return filtered;
};

export const getCategoryColor = (categoryName: string): string => {
  const name = categoryName.toLowerCase();
  if (name.includes('income') || name.includes('salary') || name.includes('earning')) {
    return 'bg-emerald-500/15 text-emerald-300 border-emerald-500/30';
  } else if (name.includes('invest') || name.includes('saving') || name.includes('mutual')) {
    return 'bg-amber-500/15 text-amber-300 border-amber-500/30';
  } else if (name.includes('expense') || name.includes('spend') || name.includes('bill') ||
             name.includes('food') || name.includes('shopping') || name.includes('transport')) {
    return 'bg-rose-500/15 text-rose-300 border-rose-500/30';
  } else if (name.includes('loan') || name.includes('emi') || name.includes('debt')) {
    return 'bg-orange-500/15 text-orange-300 border-orange-500/30';
  }
  return 'bg-slate-500/15 text-slate-400 border-slate-500/30';
};

export const DEFAULT_MAIN_CATEGORIES: MainCategory[] = PRIME_CATEGORIES.map((p) => ({
  id: p.id,
  name: p.name,
  isMain: true,
  parentId: undefined,
  subcategories: [],
  color: `${p.bgClass} ${p.textClass} ${p.borderClass}`,
}));

// ============================================================================
// Helpers
// ============================================================================

export const debounce = <T extends (...args: never[]) => void>(
  func: T,
  wait: number,
): ((...args: Parameters<T>) => void) => {
  let timeout: ReturnType<typeof setTimeout>;
  return (...args: Parameters<T>) => {
    clearTimeout(timeout);
    timeout = setTimeout(() => func(...args), wait);
  };
};

export const generateId = (): string => {
  return `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
};

export const formatCurrency = (amount: number): string => {
  return new Intl.NumberFormat('en-IN', {
    maximumFractionDigits: 0,
  }).format(Math.round(amount));
};
